import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { MainLayout } from '../../components/layout/MainLayout'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { NumberInput } from '../../components/ui/NumberInput'
import { MaskedInput } from '../../components/ui/MaskedInput'
import { Modal } from '../../components/ui/Modal'
import { SelectModal } from '../../components/ui/SelectModal'
import { MultiSelectModal } from '../../components/ui/MultiSelectModal'
import { ProductSelectModal } from '../../components/ui/ProductSelectModal'

import { ArrowLeft, ShoppingCart, User, Building2, Plus, Trash2 } from 'lucide-react'
import { clienteService } from '../../services/clienteService'
import { industriaService } from '../../services/industriaService'
import { produtoService } from '../../services/produtoService'
import { pedidoService } from '../../services/pedidoService'
import { browserNotificationService } from '../../services/browserNotificationService'
import * as indexedDB from '../../services/indexedDBService'
import type { Cliente, Industria, Produto, PedidoItem, TipoFrete } from '../../types'
import { toast } from '../../utils/toast'
import { getIndustriaColorClasses, getIndustriaInlineStyles } from '../../utils/helpers'

// Interface local para o formulário de pedido
interface PedidoIndustriaForm {
  industriaId: string
  observacao?: string // Observação específica da indústria
  ordemCompra?: string // Ordem de compra específica da indústria
  frete?: TipoFrete | '' // Tipo de frete específico da indústria
  itens: PedidoItem[]
}

export function NovoPedidoPage() {
  const navigate = useNavigate()
  
  const [clientes, setClientes] = useState<Cliente[]>([])
  const [industrias, setIndustrias] = useState<Industria[]>([])
  const [produtos, setProdutos] = useState<Produto[]>([])
  
  const [selectedCliente, setSelectedCliente] = useState<Cliente | null>(null)
  const [selectedIndustrias, setSelectedIndustrias] = useState<Industria[]>([])
  const [pedidoIndustrias, setPedidoIndustrias] = useState<PedidoIndustriaForm[]>([])
  const [observacao, setObservacao] = useState('') // Observação geral

  const [showClienteModal, setShowClienteModal] = useState(false)
  const [showIndustriasModal, setShowIndustriasModal] = useState(false)
  const [showProdutoModal, setShowProdutoModal] = useState(false)
  const [showResumoModal, setShowResumoModal] = useState(false)

  const [currentIndustriaId, setCurrentIndustriaId] = useState<string | null>(null)
  
  const getIndustriaStyle = (cor?: string | null) => {
    const classes = getIndustriaColorClasses(cor)
    const inline = getIndustriaInlineStyles(cor)
    return { classes, inline }
  }

  const loadData = useCallback(async () => {
    try {
      // Carrega primeiro do IndexedDB (mais rápido e completo)
      const [clientesLocal, industriasLocal, produtosLocal] = await Promise.all([
        indexedDB.getAll<Cliente>(indexedDB.STORES.CLIENTES),
        indexedDB.getAll<Industria>(indexedDB.STORES.INDUSTRIAS),
        indexedDB.getAll<Produto>(indexedDB.STORES.PRODUTOS),
      ])

      // Se tiver dados locais, usa eles imediatamente
      if (clientesLocal.length > 0) setClientes(clientesLocal)
      if (industriasLocal.length > 0) setIndustrias(industriasLocal)
      if (produtosLocal.length > 0) setProdutos(produtosLocal)

      // Se estiver online E não tiver dados locais suficientes, busca da API
      // Mas NÃO substitui se já tiver muitos dados locais (usa sincronização manual)
      if (navigator.onLine) {
        const needsUpdate = clientesLocal.length === 0 || industriasLocal.length === 0 || produtosLocal.length === 0
        
        if (needsUpdate) {
          try {
            const [clientesResult, industriasResult, produtosResult] = await Promise.all([
              clienteService.getAll({ status: 'ativo', limit: 1000 }),
              industriaService.getAll({ status: 'ativo', limit: 1000 }),
              produtoService.getAll({ status: 'ativo', limit: 1000 }),
            ])

            setClientes(clientesResult.data)
            setIndustrias(industriasResult.data)
            setProdutos(produtosResult.data)

            // Atualiza IndexedDB com os dados mais recentes
            await Promise.all([
              indexedDB.putMany(indexedDB.STORES.CLIENTES, clientesResult.data),
              indexedDB.putMany(indexedDB.STORES.INDUSTRIAS, industriasResult.data),
              indexedDB.putMany(indexedDB.STORES.PRODUTOS, produtosResult.data),
            ])
          } catch (onlineError) {
            console.warn('Erro ao buscar dados online, usando cache local:', onlineError)
            // Já temos os dados locais carregados, então não precisamos fazer nada
          }
        }
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao carregar dados'
      toast.error(message)
      console.error('Erro ao carregar dados:', error)
    }
  }, [])

  useEffect(() => {
    loadData()
  }, [loadData])

  const handleSelectCliente = (cliente: Cliente) => {
    setSelectedCliente(cliente)
  }

  const handleSelectIndustrias = (industriasList: Industria[]) => {
    setSelectedIndustrias(industriasList)

    // Atualizar pedidoIndustrias mantendo itens, observações e finalização existentes
    const newPedidoIndustrias = industriasList.map(industria => {
      const existing = pedidoIndustrias.find(pi => pi.industriaId === industria.id)
      return existing || { industriaId: industria.id, observacao: '', ordemCompra: '', frete: '' as TipoFrete | '', itens: [] }
    })

    setPedidoIndustrias(newPedidoIndustrias)
  }

  const getIndustriaById = (industriaId: string): Industria | undefined => {
    return industrias.find(i => i.id === industriaId)
  }

  const handleProductsConfirm = (products: Array<{ id: string; codigo: string; nome: string; quantidade?: number }>) => {
    if (!currentIndustriaId) return

    setPedidoIndustrias(prev => prev.map(pi => {
      if (pi.industriaId === currentIndustriaId) {
        // Converter produtos selecionados para PedidoItem
        const newItems = products.map(p => {
          const produtoCompleto = produtos.find(prod => prod.id === p.id)
          const existingItem = pi.itens.find(item => item.produtoId === p.id)
          return {
            produtoId: p.id,
            produto: produtoCompleto || { id: p.id, codigo: p.codigo, nome: p.nome, industriaId: currentIndustriaId, ativo: true, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
            quantidade: p.quantidade || 1,
            preco: existingItem?.preco,
          }
        })
        return {
          ...pi,
          itens: newItems
        }
      }
      return pi
    }))
  }

  const updateQuantidade = (industriaId: string, produtoId: string, quantidade: number) => {
    if (quantidade <= 0) {
      removeProduto(industriaId, produtoId)
      return
    }

    setPedidoIndustrias(prev => prev.map(pi => {
      if (pi.industriaId === industriaId) {
        return {
          ...pi,
          itens: pi.itens.map(item =>
            item.produtoId === produtoId ? { ...item, quantidade } : item
          )
        }
      }
      return pi
    }))
  }

  const parseCurrencyValue = (value: string): number | undefined => {
    const digits = value.replace(/\D/g, '')
    if (!digits) return undefined
    return parseInt(digits, 10) / 100
  }

  const updatePreco = (industriaId: string, produtoId: string, preco?: number) => {
    if (preco !== undefined && preco < 0) return

    setPedidoIndustrias(prev => prev.map(pi => {
      if (pi.industriaId === industriaId) {
        return {
          ...pi,
          itens: pi.itens.map(item =>
            item.produtoId === produtoId ? { ...item, preco } : item
          )
        }
      }
      return pi
    }))
  }

  const removeProduto = (industriaId: string, produtoId: string) => {
    setPedidoIndustrias(prev => prev.map(pi => {
      if (pi.industriaId === industriaId) {
        return {
          ...pi,
          itens: pi.itens.filter(item => item.produtoId !== produtoId)
        }
      }
      return pi
    }))
  }

  const openProdutoModal = (industriaId: string) => {
    setCurrentIndustriaId(industriaId)
    setShowProdutoModal(true)
  }

  const getTotalItens = () => {
    return pedidoIndustrias.reduce((total, pi) => 
      total + pi.itens.reduce((sum, item) => sum + item.quantidade, 0), 0
    )
  }

  const handleAbrirResumo = () => {
    if (!selectedCliente) {
      toast.error('Selecione um cliente')
      return
    }

    if (selectedIndustrias.length === 0) {
      toast.error('Selecione pelo menos uma indústria')
      return
    }

    if (!pedidoIndustrias.some(pi => pi.itens.length > 0)) {
      toast.error('Adicione pelo menos um produto ao pedido')
      return
    }

    setShowResumoModal(true)
  }

  const handleConfirmarPedido = async () => {
    try {
      // Validar tamanho das observações
      if (observacao && observacao.length > 500) {
        toast.error('Observação geral deve ter no máximo 500 caracteres')
        return
      }

      // Validar observações das indústrias
      const invalidObservacao = pedidoIndustrias.find(pi => pi.observacao && pi.observacao.length > 500)
      if (invalidObservacao) {
        toast.error('Observação da indústria deve ter no máximo 500 caracteres')
        return
      }

      // Preparar payload para a API
      const industriasPayload = pedidoIndustrias
        .filter(pi => pi.itens.length > 0)
        .map(pi => ({
          industriaId: pi.industriaId,
          observacao: pi.observacao || undefined,
          observacaoIndustria: pi.observacao || undefined,
          observacao_industria: pi.observacao || undefined,
          ordemCompra: pi.ordemCompra || undefined,
          frete: pi.frete || undefined,
          itens: pi.itens.map(item => ({
            produtoId: item.produtoId,
            quantidade: item.quantidade,
            preco: item.preco
          }))
        }))

      // Criar os pedidos (um por indústria)
      const pedidosCriados = await pedidoService.create({
        clienteId: selectedCliente!.id,
        observacao: observacao || undefined,
        industrias: industriasPayload
      })

      // Verificar se os pedidos foram criados com sucesso
      if (!pedidosCriados || pedidosCriados.length === 0) {
        throw new Error('Pedidos criados, mas resposta inválida da API')
      }

      // Atualizar status para "processando" automaticamente em todos os pedidos
      await Promise.all(
        pedidosCriados.map(pedido =>
          pedido.id ? pedidoService.updateStatus(pedido.id, 'processando') : Promise.resolve()
        )
      )

      // Disparar notificação do browser
      const clientName = selectedCliente?.razaoSocial || 'Cliente'
      const orderNumber = pedidosCriados[0].numero || `#${pedidosCriados[0].id.slice(0, 8).toUpperCase()}`
      browserNotificationService.notifyNewOrder(orderNumber, clientName)

      const quantidade = pedidosCriados.length
      toast.success(`${quantidade} ${quantidade === 1 ? 'pedido criado' : 'pedidos criados'} e enviados para processamento!`)
      navigate('/pedidos')
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao criar pedido'
      toast.error(message)
      console.error('Erro ao criar pedido:', error)
    } finally {
      setShowResumoModal(false)
    }
  }

  const getProdutosPorIndustria = (industriaId: string) => {
    return produtos.filter(p => p.industriaId === industriaId)
  }

  return (
    <MainLayout>
      <div className="space-y-4 md:space-y-6 animate-fadeIn pb-6">
        <div className="flex items-center gap-3 md:gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/pedidos')}
          >
            <ArrowLeft className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Voltar</span>
          </Button>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white">
            Novo Pedido
          </h1>
        </div>

        {/* Cliente */}
        <Card title="Cliente">
          <div className="space-y-3">
            {selectedCliente ? (
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 rounded-lg border-2 border-blue-500 bg-blue-50 dark:bg-blue-900/20 p-4">
                <div className="flex items-center gap-3">
                  <div className="rounded-full bg-blue-100 dark:bg-blue-900/40 p-3">
                    <User className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-gray-900 dark:text-white truncate">{selectedCliente.razaoSocial}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{selectedCliente.email}</p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowClienteModal(true)}
                  className="w-full sm:w-auto"
                >
                  Alterar
                </Button>
              </div>
            ) : (
              <Button
                variant="primary"
                onClick={() => setShowClienteModal(true)}
                className="w-full min-h-[56px]"
              >
                <User className="h-5 w-5 mr-2" />
                Selecionar Cliente
              </Button>
            )}
          </div>
        </Card>

        {/* Indústrias */}
        <Card title="Indústrias">
          <div className="space-y-3">
            <Button
              variant={selectedIndustrias.length > 0 ? 'outline' : 'primary'}
              onClick={() => setShowIndustriasModal(true)}
              className="w-full min-h-[56px]"
            >
              <Building2 className="h-5 w-5 mr-2" />
              {selectedIndustrias.length > 0
                ? `${selectedIndustrias.length} indústria(s) selecionada(s)`
                : 'Selecionar Indústrias'}
            </Button>

            {selectedIndustrias.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {selectedIndustrias.map(industria => {
                  const { classes, inline } = getIndustriaStyle(industria.cor)

                  return (
                  <div
                    key={industria.id}
                    className={`inline-flex items-center gap-2 rounded-lg px-3 py-1 text-sm font-medium border-2 ${classes.border} ${classes.bg} ${classes.text}`}
                    style={inline ? {
                      borderColor: inline.borderColor,
                      backgroundColor: inline.backgroundColor,
                      color: inline.color,
                    } : undefined}
                  >
                    <span
                      className="h-2.5 w-2.5 rounded-full"
                      style={{ backgroundColor: inline ? inline.cor : classes.cor }}
                    />
                    {industria.nomeFantasia}
                  </div>
                )})}
              </div>
            )}
          </div>
        </Card>

        {/* Produtos por Indústria */}
        {pedidoIndustrias.length > 0 && (
          <Card title="Produtos">
            <div className="space-y-4">
              {pedidoIndustrias.map(pedidoIndustria => {
                const industria = getIndustriaById(pedidoIndustria.industriaId)
                const { classes, inline } = getIndustriaStyle(industria?.cor)

                return (
                <div
                  key={pedidoIndustria.industriaId}
                  className={`rounded-lg border-2 shadow-sm ${classes.border} ${classes.bgLight} overflow-hidden transition-shadow hover:shadow-md`}
                  style={inline ? {
                    borderColor: inline.borderColor,
                    backgroundColor: inline.backgroundColorLight,
                  } : undefined}
                >
                  <div
                    className={`p-3 md:p-4 ${classes.bg}`}
                    style={inline ? { backgroundColor: inline.backgroundColor } : undefined}
                  >
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <span
                          className="h-3 w-3 rounded-full ring-2 ring-white dark:ring-gray-800"
                          style={{ backgroundColor: inline ? inline.cor : classes.cor }}
                        />
                        <h3 className={`font-semibold text-base md:text-lg ${classes.text}`} style={inline ? { color: inline.color } : undefined}>
                          {industria?.nomeFantasia || 'Indústria'}
                        </h3>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openProdutoModal(pedidoIndustria.industriaId)}
                        className="w-full sm:w-auto"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        <span className="sm:inline">Adicionar Produto</span>
                      </Button>
                    </div>
                  </div>

                  <div className="p-3 md:p-4">
                    {pedidoIndustria.itens.length === 0 ? (
                      <p className="text-center text-gray-500 dark:text-gray-400 py-6 text-base">
                        Nenhum produto adicionado
                      </p>
                    ) : (
                      <div className="space-y-4">
                        {/* Layout mobile-first: cards em vez de tabela */}
                        <div className="block md:hidden space-y-4">
                          {pedidoIndustria.itens.map(item => (
                            <div
                              key={item.produtoId}
                              className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-3"
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                                    Código: {item.produto?.codigo || 'N/A'}
                                  </p>
                                  <p className="font-medium text-gray-900 dark:text-white">
                                    {item.produto?.nome || 'Produto não encontrado'}
                                  </p>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() =>
                                    removeProduto(pedidoIndustria.industriaId, item.produtoId)
                                  }
                                  title="Excluir produto"
                                  className="flex-shrink-0"
                                >
                                  <Trash2 className="h-5 w-5 text-red-500" />
                                </Button>
                              </div>

                              <div className="space-y-3">
                                <NumberInput
                                  label="Quantidade"
                                  value={item.quantidade}
                                  onChange={value =>
                                    updateQuantidade(
                                      pedidoIndustria.industriaId,
                                      item.produtoId,
                                      value
                                    )
                                  }
                                  min={1}
                                  showControls={true}
                                />

                                <div>
                                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                                    Preço Unitário (R$)
                                  </label>
                                  <MaskedInput
                                    mask="currency"
                                    value={item.preco !== undefined ? item.preco.toFixed(2).replace('.', ',') : ''}
                                    onChange={value =>
                                      updatePreco(
                                        pedidoIndustria.industriaId,
                                        item.produtoId,
                                        parseCurrencyValue(value)
                                      )
                                    }
                                    placeholder="0,00"
                                  />
                                </div>

                                {item.preco !== undefined && item.preco > 0 && (
                                  <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                                    <div className="flex justify-between items-center">
                                      <span className="text-sm text-gray-600 dark:text-gray-400">Subtotal:</span>
                                      <span className="text-lg font-bold text-gray-900 dark:text-white">
                                        R$ {(item.quantidade * item.preco).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                      </span>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Layout desktop: tabela tradicional */}
                        <div className="hidden md:block overflow-x-auto">
                          <table className="w-full">
                            <thead className="border-b border-gray-200 dark:border-gray-700">
                              <tr>
                                <th className="pb-3 text-left text-sm font-semibold text-gray-900 dark:text-white">
                                  Código
                                </th>
                                <th className="pb-3 text-left text-sm font-semibold text-gray-900 dark:text-white">
                                  Nome
                                </th>
                                <th className="pb-3 text-left text-sm font-semibold text-gray-900 dark:text-white">
                                  Quantidade
                                </th>
                                <th className="pb-3 text-left text-sm font-semibold text-gray-900 dark:text-white">
                                  Preço Unitário (R$)
                                </th>
                                <th className="pb-3 text-left text-sm font-semibold text-gray-900 dark:text-white">
                                  Subtotal (R$)
                                </th>
                                <th className="pb-3 text-right text-sm font-semibold text-gray-900 dark:text-white">
                                  Ação
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                              {pedidoIndustria.itens.map(item => (
                                <tr
                                  key={item.produtoId}
                                  className="hover:bg-gray-50 dark:hover:bg-gray-700/50"
                                >
                                  <td className="py-3 text-sm font-medium text-gray-900 dark:text-white">
                                    {item.produto?.codigo || 'N/A'}
                                  </td>
                                  <td className="py-3 text-base text-gray-700 dark:text-gray-300 whitespace-normal break-words max-w-md">
                                    {item.produto?.nome || 'Produto não encontrado'}
                                  </td>
                                  <td className="py-3">
                                    <NumberInput
                                      value={item.quantidade}
                                      onChange={value =>
                                        updateQuantidade(
                                          pedidoIndustria.industriaId,
                                          item.produtoId,
                                          value
                                        )
                                      }
                                      min={1}
                                      showControls={false}
                                      className="w-24"
                                    />
                                  </td>
                                  <td className="py-3">
                                    <MaskedInput
                                      mask="currency"
                                      value={item.preco !== undefined ? item.preco.toFixed(2).replace('.', ',') : ''}
                                      onChange={value =>
                                        updatePreco(
                                          pedidoIndustria.industriaId,
                                          item.produtoId,
                                          parseCurrencyValue(value)
                                        )
                                      }
                                      placeholder="0,00"
                                      className="w-28"
                                    />
                                  </td>
                                  <td className="py-3 text-sm font-semibold text-gray-900 dark:text-white">
                                    {item.preco !== undefined && item.preco > 0 ? (
                                      `R$ ${(item.quantidade * item.preco).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                    ) : (
                                      <span className="text-gray-400 dark:text-gray-500">-</span>
                                    )}
                                  </td>
                                  <td className="py-3 text-right">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() =>
                                        removeProduto(pedidoIndustria.industriaId, item.produtoId)
                                      }
                                      title="Excluir produto"
                                    >
                                      <Trash2 className="h-4 w-4 text-red-500" />
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Subtotal da Indústria */}
                    {pedidoIndustria.itens.length > 0 && (() => {
                      const subtotal = pedidoIndustria.itens.reduce((sum, item) => 
                        sum + (item.quantidade * (item.preco || 0)), 0
                      )
                      
                      return subtotal > 0 ? (
                        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                          <div className="flex justify-end">
                            <div className="text-right">
                              <span className="text-sm text-gray-600 dark:text-gray-400">Subtotal desta indústria: </span>
                              <span className="text-lg font-bold text-gray-900 dark:text-white">
                                R$ {subtotal.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </span>
                            </div>
                          </div>
                        </div>
                      ) : null
                    })()}

                    {/* Finalização da Indústria */}
                    <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 space-y-4">
                      <div>
                        <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                          Ordem de Compra (opcional)
                        </label>
                        <Input
                          type="text"
                          value={pedidoIndustria.ordemCompra || ''}
                          onChange={e => {
                            setPedidoIndustrias(prev => prev.map(pi =>
                              pi.industriaId === pedidoIndustria.industriaId
                                ? { ...pi, ordemCompra: e.target.value }
                                : pi
                            ))
                          }}
                          maxLength={100}
                          placeholder="Ex: OC-2024-001"
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Número da ordem de compra para esta indústria
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                          Frete (opcional)
                        </label>
                        <select
                          value={pedidoIndustria.frete || ''}
                          onChange={e => {
                            setPedidoIndustrias(prev => prev.map(pi =>
                              pi.industriaId === pedidoIndustria.industriaId
                                ? { ...pi, frete: e.target.value as TipoFrete | '' }
                                : pi
                            ))
                          }}
                          className="flex min-h-[44px] w-full rounded-lg border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-4 py-3 text-base text-gray-900 dark:text-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all"
                        >
                          <option value="">Selecione o tipo de frete</option>
                          <option value={'CIF'}>CIF</option>
                          <option value={'FOB'}>FOB</option>
                        </select>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Tipo de frete para esta indústria
                        </p>
                      </div>
                    </div>

                    {/* Observação da Indústria */}
                    <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                      <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                        Observação para esta indústria (opcional)
                      </label>
                      <textarea
                        value={pedidoIndustria.observacao || ''}
                        onChange={e => {
                          setPedidoIndustrias(prev => prev.map(pi =>
                            pi.industriaId === pedidoIndustria.industriaId
                              ? { ...pi, observacao: e.target.value }
                              : pi
                          ))
                        }}
                        rows={3}
                        maxLength={500}
                        className="w-full rounded-lg border-2 border-gray-300 bg-white px-4 py-3 text-base dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:border-primary focus:ring-2 focus:ring-primary transition-all"
                        placeholder="Ex: Embalar separadamente, entregar até 10h, etc..."
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
                        {pedidoIndustria.observacao?.length || 0}/500 caracteres
                      </p>
                    </div>
                  </div>
                </div>
              )})}
            </div>
          </Card>
        )}

        {/* Observações Gerais */}
        <Card title="Observações Gerais">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                Observações Gerais (opcional)
              </label>
              <textarea
                value={observacao}
                onChange={e => setObservacao(e.target.value)}
                rows={4}
                maxLength={500}
                className="w-full rounded-lg border-2 border-gray-300 bg-white px-4 py-3 text-base dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:border-primary focus:ring-2 focus:ring-primary transition-all"
                placeholder="Observações gerais sobre o pedido..."
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
                {observacao.length}/500 caracteres
              </p>
            </div>
          </div>
        </Card>

        {/* Resumo */}
        <Card>
          <div className="space-y-3">
            <div className="rounded-lg bg-blue-50 p-4 dark:bg-blue-900/20">
              <div className="flex items-center justify-between">
                <span className="text-lg font-semibold text-blue-900 dark:text-blue-100">
                  Total de Itens:
                </span>
                <span className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {getTotalItens()}
                </span>
              </div>
            </div>

            {(() => {
              const valorTotal = pedidoIndustrias.reduce((total, pi) => 
                total + pi.itens.reduce((sum, item) => 
                  sum + (item.quantidade * (item.preco || 0)), 0
                ), 0
              )
              
              return valorTotal > 0 ? (
                <div className="rounded-lg bg-green-50 p-4 dark:bg-green-900/20">
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-semibold text-green-900 dark:text-green-100">
                      Valor Total:
                    </span>
                    <span className="text-2xl font-bold text-green-600 dark:text-green-400">
                      R$ {valorTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
              ) : null
            })()}
          </div>
          <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4">
            <Button variant="outline" onClick={() => navigate('/pedidos')} className="w-full sm:w-auto order-2 sm:order-1">
              Cancelar
            </Button>
            <Button onClick={handleAbrirResumo} size="lg" className="w-full sm:w-auto order-1 sm:order-2">
              <ShoppingCart className="mr-2 h-5 w-5" />
              Revisar e Criar Pedido
            </Button>
          </div>
        </Card>
      </div>

      {/* Modais */}
      <SelectModal
        isOpen={showClienteModal}
        onClose={() => setShowClienteModal(false)}
        onSelect={handleSelectCliente}
        items={clientes}
        title="Selecionar Cliente"
        searchPlaceholder="Buscar por nome ou email..."
        getItemKey={item => item.id}
        getItemLabel={item => item.razaoSocial}
        getItemDescription={item => item.email || ''}
        selectedId={selectedCliente?.id}
      />

      <MultiSelectModal
        isOpen={showIndustriasModal}
        onClose={() => setShowIndustriasModal(false)}
        onConfirm={handleSelectIndustrias}
        items={industrias}
        title="Selecionar Indústrias"
        searchPlaceholder="Buscar indústria..."
        getItemKey={item => item.id}
        getItemLabel={item => item.nomeFantasia}
        getItemDescription={item => item.cnpj || ''}
        selectedIds={selectedIndustrias.map(i => i.id)}
      />

      <ProductSelectModal
        isOpen={showProdutoModal}
        onClose={() => setShowProdutoModal(false)}
        onConfirm={handleProductsConfirm}
        items={currentIndustriaId ? getProdutosPorIndustria(currentIndustriaId) : []}
        selectedItems={
          currentIndustriaId
            ? pedidoIndustrias
                .find(pi => pi.industriaId === currentIndustriaId)
                ?.itens.map(item => ({
                  id: item.produtoId,
                  codigo: item.produto?.codigo || '',
                  nome: item.produto?.nome || '',
                  quantidade: item.quantidade,
                })) || []
            : []
        }
        title="Selecionar Produtos"
        searchPlaceholder="Buscar por código ou nome..."
      />

      {/* Modal de Resumo do Pedido */}
      <Modal
        isOpen={showResumoModal}
        onClose={() => setShowResumoModal(false)}
        title="Resumo do Pedido"
        size="xl"
      >
        <div className="space-y-6">
          {/* Cliente */}
          <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
            <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white">
              <User className="h-4 w-4" />
              Cliente
            </h3>
            <div className="space-y-1 text-sm">
              <p className="font-medium text-gray-900 dark:text-white">
                {selectedCliente?.razaoSocial}
              </p>
              <p className="text-gray-600 dark:text-gray-400">
                CNPJ: {selectedCliente?.cnpj}
              </p>
              <p className="text-gray-600 dark:text-gray-400">
                Email: {selectedCliente?.email}
              </p>
            </div>
          </div>

          {/* Indústrias e Produtos */}
          <div className="space-y-4">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white">
              <Building2 className="h-4 w-4" />
              Indústrias e Produtos
            </h3>
            
            {pedidoIndustrias
              .filter(pi => pi.itens.length > 0)
              .map(pedidoIndustria => {
                const industria = getIndustriaById(pedidoIndustria.industriaId)
                const { classes, inline } = getIndustriaStyle(industria?.cor)
                const subtotal = pedidoIndustria.itens.reduce((sum, item) => sum + item.quantidade, 0)

                return (
                  <div
                    key={pedidoIndustria.industriaId}
                    className={`rounded-lg border-2 shadow-sm ${classes.border} ${classes.bgLight} overflow-hidden`}
                    style={inline ? {
                      borderColor: inline.borderColor,
                      backgroundColor: inline.backgroundColorLight,
                    } : undefined}
                  >
                    <div
                      className={`px-4 py-3 ${classes.bg}`}
                      style={inline ? { backgroundColor: inline.backgroundColor } : undefined}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span
                            className="h-2.5 w-2.5 rounded-full"
                            style={{ backgroundColor: inline ? inline.cor : classes.cor }}
                          />
                          <h4 className={`text-sm font-semibold ${classes.text}`} style={inline ? { color: inline.color } : undefined}>
                            {industria?.nomeFantasia || 'Indústria'}
                          </h4>
                        </div>
                        <span className={`text-sm font-medium ${classes.text}`} style={inline ? { color: inline.color } : undefined}>
                          Subtotal: {subtotal} {subtotal === 1 ? 'item' : 'itens'}
                        </span>
                      </div>
                    </div>
                    
                    <div className="p-4">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-gray-200 dark:border-gray-700">
                            <th className="pb-2 text-left text-xs font-medium text-gray-600 dark:text-gray-400">
                              Código
                            </th>
                            <th className="pb-2 text-left text-xs font-medium text-gray-600 dark:text-gray-400">
                              Produto
                            </th>
                            <th className="pb-2 text-right text-xs font-medium text-gray-600 dark:text-gray-400">
                              Quantidade
                            </th>
                            <th className="pb-2 text-right text-xs font-medium text-gray-600 dark:text-gray-400">
                              Preço (R$)
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                          {pedidoIndustria.itens.map(item => (
                            <tr key={item.produtoId}>
                              <td className="py-2 text-sm text-gray-900 dark:text-white">
                                {item.produto?.codigo}
                              </td>
                              <td className="py-2 text-base text-gray-700 dark:text-gray-300 whitespace-normal break-words">
                                {item.produto?.nome}
                              </td>
                              <td className="py-2 text-right text-sm font-medium text-gray-900 dark:text-white">
                                {item.quantidade}
                              </td>
                              <td className="py-2 text-right text-sm text-gray-700 dark:text-gray-300">
                                {item.preco !== undefined
                                  ? `R$ ${item.preco.toFixed(2).replace('.', ',')}`
                                  : '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>

                      {/* Finalização da Indústria no Resumo */}
                      {(pedidoIndustria.ordemCompra || pedidoIndustria.frete) && (
                        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                          <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                            Informações de finalização:
                          </p>
                          <div className="flex flex-wrap gap-3 text-sm">
                            {pedidoIndustria.ordemCompra && (
                              <span className="text-gray-900 dark:text-white font-medium">
                                OC: {pedidoIndustria.ordemCompra}
                              </span>
                            )}
                            {pedidoIndustria.frete && (
                              <span className="text-gray-900 dark:text-white font-medium">
                                Frete: {pedidoIndustria.frete}
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Observação da Indústria no Resumo */}
                      {pedidoIndustria.observacao && (
                        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                          <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                            Observação:
                          </p>
                          <p className="text-sm text-gray-700 dark:text-gray-300 italic">
                            {pedidoIndustria.observacao}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
          </div>

          {/* Observações Gerais */}
          {observacao && (
            <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
              <h3 className="mb-2 text-sm font-semibold text-gray-900 dark:text-white">
                Observações Gerais
              </h3>
              <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                {observacao}
              </p>
            </div>
          )}

          {/* Total Geral */}
          <div className="rounded-lg bg-blue-50 p-4 dark:bg-blue-900/20">
            <div className="flex items-center justify-between">
              <span className="text-base font-semibold text-blue-900 dark:text-blue-100">
                Total Geral:
              </span>
              <span className="text-xl font-bold text-blue-600 dark:text-blue-400">
                {getTotalItens()} {getTotalItens() === 1 ? 'item' : 'itens'}
              </span>
            </div>
            <div className="mt-2 text-sm text-blue-700 dark:text-blue-300">
              {pedidoIndustrias.filter(pi => pi.itens.length > 0).length} {pedidoIndustrias.filter(pi => pi.itens.length > 0).length === 1 ? 'indústria' : 'indústrias'}
            </div>
          </div>

          {/* Ações */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button variant="outline" onClick={() => setShowResumoModal(false)}>
              Voltar e Editar
            </Button>
            <Button onClick={handleConfirmarPedido}>
              <ShoppingCart className="mr-2 h-4 w-4" />
              Confirmar Pedido
            </Button>
          </div>
        </div>
      </Modal>

    </MainLayout>
  )
}
